import React, { useEffect, useState } from 'react';
import { Input, Switch, Textarea } from '@cokitchen/cokitchen-components';
import ShopStatus from 'components/shop-status';
import useNewShopContext from 'contexts/create-shop.context';
import LiveTag from 'components/live-tag';
import { ENUM_SHOP_STATUS } from 'utils/enum';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

const EMAIL_REGEX = /^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const PHONE_REGEX = /^(?:\+234|0)[789][01]\d{8}$/;

const CreateShopDetails = () => {
  const {
    createShopForm,

    setCreateShopForm,
    setDisabled
  } = useNewShopContext();
  const [address, setAddress] = useState('');

  useEffect(() => {
    const disabled = ![
      createShopForm?.name,
      createShopForm?.short_text,
      createShopForm?.description
    ].every(Boolean);
    if (createShopForm?.address.address_line) {
      setAddress(createShopForm.address.address_line);
    }
    setDisabled(disabled);
  }, [createShopForm]);

  const getAddressDetails = async (value: string) => {
    setAddress(value);
    const results = await geocodeByAddress(value);

    if (results) {
      const latLng = await getLatLng(results[0]);
      if (latLng) {
        const addressComponents = results[0];

        setCreateShopForm({
          ...createShopForm,
          address: {
            address_line: value,
            latitude: latLng.lat,
            longitude: latLng.lng,
            state:
              addressComponents.address_components.find((addr) => {
                return addr.types.includes('administrative_area_level_1');
              })?.long_name ?? '',
            country:
              addressComponents.address_components.find((addr) => {
                return addr.types.includes('country');
              })?.long_name ?? '',
            country_code:
              addressComponents.address_components.find((addr) => {
                return addr.types.includes('country');
              })?.short_name ?? ''
          }
        });
      }
    }
  };

  const live = createShopForm.live === ENUM_SHOP_STATUS.active;
  const searchOptions = {
    componentRestrictions: { country: ['ng'] }
  };

  return (
    <div className='space-y-4'>
      <Input
        type='text'
        label='Shop Name'
        placeholder='Enter shop name'
        className='w-full mt-4'
        inputClass='px-[20px] py-[14px]'
        value={createShopForm?.name}
        onChange={(e) => setCreateShopForm({ ...createShopForm, name: e.target.value })}
      />
      <Input
        type='text'
        label='Create shop Tagline'
        placeholder='Enter shop tagline'
        className='w-full mt-4'
        inputClass='px-[20px] py-[14px]'
        value={createShopForm?.short_text}
        maxLength={15}
        onChange={(e) => setCreateShopForm({ ...createShopForm, short_text: e.target.value })}
      />

      <Textarea
        className='w-full mt-1'
        placeholder='Describe the shop'
        value={createShopForm?.description}
        onChange={(e) => setCreateShopForm({ ...createShopForm, description: e.target.value })}
        label='Shop Description'
      />
      <Input
        type='text'
        label='Phone Number'
        placeholder='Enter phone number'
        className='w-full mt-4'
        inputClass='px-[20px] py-[14px]'
        value={createShopForm?.phone}
        maxLength={14}
        onChange={(e) => {
          setCreateShopForm({ ...createShopForm, phone: e.target.value });
        }}
      />
      {createShopForm.phone && !PHONE_REGEX.test(createShopForm.phone) && (
        <p className='text-red-5'>Enter valid phone number</p>
      )}
      <Input
        type='text'
        label='Email Address'
        placeholder='Enter email address'
        className='w-full mt-4'
        inputClass='px-[20px] py-[14px]'
        value={createShopForm?.email}
        onChange={(e) => {
          setCreateShopForm({ ...createShopForm, email: e.target.value });
        }}
      />
      {createShopForm.email && !EMAIL_REGEX.test(createShopForm.email) && (
        <p className='text-red-5 '>Enter valid email address</p>
      )}

      <PlacesAutocomplete
        value={address}
        debounce={600}
        onChange={setAddress}
        onSelect={getAddressDetails}
        searchOptions={searchOptions}>
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div>
            <Input
              {...getInputProps({
                placeholder: 'Enter address...',
                className: 'mb-2'
              })}
              label='Shop Address'
            />
            <div className=' '>
              {loading && <div>Loading...</div>}
              {suggestions.map((suggestion, i) => {
                const className = 'px-3 py-2 ';
                const style = suggestion.active
                  ? {
                      backgroundColor: '#F5F5F5',
                      transition: 'all .3s',
                      cursor: 'pointer'
                    }
                  : { backgroundColor: '#ffffff', cursor: 'pointer' };

                return (
                  <div
                    {...getSuggestionItemProps(suggestion, {
                      className,
                      style
                    })}
                    key={i}>
                    <span>{suggestion.description}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </PlacesAutocomplete>

      <div className='my-8'>
        <ShopStatus
          comment={createShopForm?.brandStatustext}
          setComment={(k) => setCreateShopForm({ ...createShopForm, brandStatustext: k })}
          className='my-6'
          selectedShopStatus={createShopForm.shopStatus}
          setSelectedShopStatus={(d) => setCreateShopForm({ ...createShopForm, shopStatus: d })}
        />
      </div>

      <div className='border py-[20px] px-[24px] border-[#EDEDEE] rounded-xl flex justify-between my-8'>
        <div className='flex items-center justify-start gap-7'>
          <p className='text-xs font-medium'>Brand Active</p>
          <LiveTag live={live} />
        </div>
        <Switch
          active={live}
          onChange={(e) =>
            setCreateShopForm({
              ...createShopForm,
              live: e ? ENUM_SHOP_STATUS.active : ENUM_SHOP_STATUS.inactive
            })
          }
          label='Roll Back'
        />
      </div>
    </div>
  );
};

export default CreateShopDetails;
